import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { types } from '@Pages/Heatmap/HeatmapTooltipHeaders/constants';
import { ButtonWithIcon, Button } from '@els/els-react--button';
import { Icon } from '@els/els-react--icon';

import './TooltipContentOptions.scss';

function TooltipContentOptions({
  isRow,
  type,
  isReady,
  hasStructure,
  hasTargets,
  isSortByActivityActive,
  isAscending,
  sortByActivity = () => {},
  isLimitDisabled,
  limitTo = () => {},
  onCopyStructureToClipboard = () => {},
  onCopyStructureToQuery = () => {},
  onCopyStructureToFilter = () => {},
  onShowResultsContextForSelection = () => {},
}) {
  const isSubstance = type === types.substance;
  const isTarget = type === types.target;

  return (
    <div className={cn('heatmap-tooltip-content-options', { 'heatmap-tooltip-content-options--has-structure': hasStructure })}>
      {isReady ? (
        <ul className="heatmap-tooltip-content-options__sections">
          {/* Sort by activity */}
          <li
            className={cn('heatmap-tooltip-content-options__section', {
              'heatmap-tooltip-content-options__section--active': isSortByActivityActive,
            })}>
            <div className="heatmap-tooltip-content-options__option">
              <Button
                data-e2e="sort-by-activity"
                type="link"
                className="e2e-sort-by-activity"
                onClick={() => sortByActivity()}
                aria-label="Sort by activity"
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="Sort by activity">
                {isSortByActivityActive && <Icon sprite="Checkmark">active</Icon>}
                Sort by activity
              </Button>
              {isSortByActivityActive && (
                <div className="heatmap-tooltip-content-options__sort-direction">
                  <ButtonWithIcon
                    data-e2e="sort-by-activity-desc"
                    type="link"
                    sprite="ArrowDown"
                    iconOnly
                    disabled={!isAscending}
                    onClick={() => sortByActivity(false)}
                    className={`${cn({ active: !isAscending })} e2e-sort-by-activity-desc`}
                    aria-label="Sort by activity descending"
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-type="productfeatureused"
                    data-tracking-action="Sort by activity">
                    Sort by activity descending
                  </ButtonWithIcon>

                  <ButtonWithIcon
                    data-e2e="sort-by-activity-asc"
                    type="link"
                    sprite="ArrowUp"
                    iconOnly
                    disabled={isAscending}
                    onClick={() => sortByActivity(true)}
                    className={`${cn({ active: isAscending })} e2e-sort-by-activity-asc`}
                    aria-label="Sort by activity ascending"
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-type="productfeatureused"
                    data-tracking-action="Sort by activity">
                    Sort by activity ascending
                  </ButtonWithIcon>
                </div>
              )}
            </div>
          </li>
          {/* Limit to & exclude */}
          <li className="heatmap-tooltip-content-options__section">
            <ButtonWithIcon
              data-e2e="heatmap-limit-to"
              type="link"
              sprite="Screenshot"
              iconRight
              className="heatmap-tooltip-content-options__option e2e-heatmap-limit-to"
              onClick={() => limitTo()}
              disabled={isLimitDisabled}
              aria-label={`Limit to selected ${isRow ? 'row(s)' : 'column(s)'}`}
              data-click-delegation
              data-tracking="true"
              data-tracking-type="productfeatureused"
              data-tracking-action-text-content>
              {`Limit to selected ${isRow ? 'row(s)' : 'column(s)'}`}
            </ButtonWithIcon>

            <ButtonWithIcon
              data-e2e="heatmap-exclude"
              type="link"
              sprite="Forbidden"
              iconRight
              className="heatmap-tooltip-content-options__option e2e-heatmap-exclude"
              aria-label={`Exclude selected ${isRow ? 'row(s)' : 'column(s)'}`}
              disabled={isLimitDisabled}
              onClick={() => limitTo({ exclude: true })}
              data-click-delegation
              data-tracking="true"
              data-tracking-type="productfeatureused"
              data-tracking-action-text-content>
              {`Exclude selected ${isRow ? 'row(s)' : 'column(s)'}`}
            </ButtonWithIcon>
          </li>
          {/* Copy structure */}
          {hasStructure && (
            <li className="heatmap-tooltip-content-options__section">
              <div className="heatmap-tooltip-content-options__option els-btn els-btn-sm els-btn-link">
                <span>Copy structure to</span>
                <svg className="els-btn-icon">
                  <use xlinkHref="#icon-chevron-right" />
                </svg>
                <div className="heatmap-tooltip-content-options__sub-options">
                  <div className="heatmap-tooltip-content-options__section">
                    <Button
                      data-e2e="heatmap-copy-to-clipboard"
                      type="link"
                      aria-label="Copy structure to Clipboard"
                      onClick={onCopyStructureToClipboard}
                      className="heatmap-tooltip-content-options__option e2e-heatmap-copy-to-clipboard"
                      data-click-delegation
                      data-tracking="true"
                      data-tracking-type="productfeatureused"
                      data-tracking-action="Copy structure to Clipboard">
                      Clipboard
                    </Button>

                    <Button
                      data-e2e="heatmap-copy-to-query"
                      type="link"
                      aria-label="Copy structure to Query"
                      onClick={onCopyStructureToQuery}
                      className="heatmap-tooltip-content-options__option e2e-heatmap-copy-to-query"
                      data-click-delegation
                      data-tracking="true"
                      data-tracking-type="productfeatureused"
                      data-tracking-action="Copy structure to Query">
                      Query
                    </Button>
                  </div>
                </div>
              </div>

              <Button
                data-e2e="heatmap-use-as-filter"
                type="link"
                aria-label="Use as filter"
                onClick={onCopyStructureToFilter}
                className="heatmap-tooltip-content-options__option e2e-heatmap-use-as-filter"
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="Use as filter">
                Use as filter
              </Button>
            </li>
          )}
          {/* Details for Targets */}
          {isTarget && (
            <li className="heatmap-tooltip-content-options__button_link">
              <Button
                data-e2e="heatmap-target-summary"
                type="link"
                aria-label="Target summary"
                disabled={!hasTargets}
                onClick={onShowResultsContextForSelection}
                className="heatmap-tooltip-content-options__option e2e-heatmap-target-summary"
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="Target summary">
                Target summary
              </Button>
            </li>
          )}
          {/* Details for Substances */}
          {isSubstance && (
            <li className="heatmap-tooltip-content-options__button_link">
              <Button
                data-e2e="heatmap-all-substance-details"
                type="link"
                aria-label="All substance details"
                onClick={onShowResultsContextForSelection}
                className="e2e-heatmap-all-substance-details"
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="All substance details">
                All substance details
              </Button>
            </li>
          )}
        </ul>
      ) : (
        <SimpleSpinner className="options-preview" />
      )}
    </div>
  );
}

TooltipContentOptions.propTypes = {
  isRow: PropTypes.bool,
  type: PropTypes.string,
  isReady: PropTypes.bool,
  hasStructure: PropTypes.bool,
  hasTargets: PropTypes.bool,
  isSortByActivityActive: PropTypes.bool,
  isAscending: PropTypes.bool,
  sortByActivity: PropTypes.func,
  isLimitDisabled: PropTypes.bool,
  limitTo: PropTypes.func,
  onCopyStructureToClipboard: PropTypes.func,
  onCopyStructureToQuery: PropTypes.func,
  onCopyStructureToFilter: PropTypes.func,
  onShowResultsContextForSelection: PropTypes.func,
};

export default TooltipContentOptions;
